import React, { useEffect, useState } from "react";

function clampStep(prev, base, jitter, min, max) {
  // small drift around prev value (smooth)
  const drift = (Math.random() - 0.5) * jitter;
  let next = prev + drift;
  if (next < min) next = min + Math.random() * (jitter / 2);
  if (next > max) next = max - Math.random() * (jitter / 2);
  // pull toward base lightly
  next = next * 0.85 + base * 0.15;
  return next;
}

export default function CommandCenter() {
  const [data, setData] = useState({ sales: 218, customers: 642, autos: 3120, marketing: 168 });
  const [graph, setGraph] = useState(Array.from({ length: 24 }, (_, i) => 50 + Math.sin(i * 0.4) * 12 + (Math.random() - 0.5) * 6));

  useEffect(() => {
    const t = setInterval(() => {
      setData((d) => ({
        sales: Math.round(clampStep(d.sales, 220, 8, 195, 245)),
        customers: Math.round(clampStep(d.customers, 650, 18, 595, 705)),
        autos: Math.round(clampStep(d.autos, 3120, 60, 2950, 3300)),
        marketing: Math.round(clampStep(d.marketing, 170, 6, 150, 188)),
      }));
      setGraph((g) => {
        const last = g[g.length - 1];
        const nv = Math.max(35, Math.min(80, last + (Math.random() - 0.5) * 5));
        return [...g.slice(1), nv];
      });
    }, 1600);
    return () => clearInterval(t);
  }, []);

  const bars = [
    { l: "Sales today", v: `$${data.sales}k`, c: "#FF007F", pct: ((data.sales - 195) / 50) * 100 },
    { l: "Active customers", v: data.customers.toLocaleString(), c: "#00F0FF", pct: ((data.customers - 595) / 110) * 100 },
    { l: "Automations run", v: data.autos.toLocaleString(), c: "#B200FF", pct: ((data.autos - 2950) / 350) * 100 },
    { l: "Marketing reach", v: `${data.marketing}k`, c: "#FF6000", pct: ((data.marketing - 150) / 38) * 100 },
  ];

  return (
    <section className="relative py-28 px-6" data-testid="command-center-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-mono text-[11px] tracking-[0.4em] text-cyan-300/80 mb-3">◆ COMMAND CENTER</p>
          <h2 className="text-4xl sm:text-5xl font-[Outfit] font-bold">Your business — <span className="text-gradient-cyan">live, in one hologram.</span></h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 glass-strong holo-border rounded-3xl p-6 relative overflow-hidden scan-line">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-xs uppercase tracking-widest text-white/55">Revenue Pulse · Live</div>
              <div className="font-mono text-[10px] text-emerald-400">● STREAMING</div>
            </div>
            <svg viewBox="0 0 400 160" className="w-full h-44">
              <defs>
                <linearGradient id="grad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#00F0FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              {[0, 40, 80, 120, 160].map((y) => (
                <line key={y} x1="0" x2="400" y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
              ))}
              <polyline
                fill="url(#grad)"
                stroke="#00F0FF"
                strokeWidth="2"
                points={`0,160 ${graph.map((v, i) => `${(i / (graph.length - 1)) * 400},${160 - v * 1.5}`).join(" ")} 400,160`}
              />
              <polyline
                fill="none"
                stroke="#FF007F"
                strokeWidth="1.5"
                strokeDasharray="4 4"
                points={graph.map((v, i) => `${(i / (graph.length - 1)) * 400},${160 - v * 1.2 - 8}`).join(" ")}
              />
            </svg>
          </div>

          <div className="space-y-4">
            {bars.map((b) => (
              <div key={b.l} className="glass rounded-2xl p-5 holo-border" data-testid={`cc-stat-${b.l.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/45">{b.l}</div>
                <div className="mt-1 text-3xl font-[Outfit] font-bold" style={{ color: b.c }}>{b.v}</div>
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full transition-all duration-700" style={{ width: `${Math.max(15, Math.min(95, b.pct))}%`, background: b.c, boxShadow: `0 0 8px ${b.c}` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
